import React, { useEffect, useMemo, useRef, useState } from "react";
import { Select, Spin } from "antd";
import { useLazyQuery } from "@apollo/client";
import { SEARCH_CARS } from "../../graphql/AdminQueries";
import { getItem } from "../../utility/localStorageControl";

const { Option } = Select;

const MIN_SEARCH_LEN = 3;
const DEFAULT_LIMIT = 20;
const DEBOUNCE_MS = 350;

const SelectCarAsync = ({
  tokenGet,
  required,
  onCarSelected,
  showCarOwnerCompany = false,
  limit = DEFAULT_LIMIT,
  branchOffice: branchOfficeProp,
  ...rest
}) => {
  const [searchText, setSearchText] = useState("");
  const [cars, setCars] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);
  const requestSeq = useRef(0);

  const [fetchCars, { loading }] = useLazyQuery(SEARCH_CARS, {
    fetchPolicy: "network-only",
  });

  const branchOfficeForUser = useMemo(() => {
    // If branchOffice prop is provided, use it; otherwise auto-detect from localStorage
    if (branchOfficeProp != null) {
      return typeof branchOfficeProp === "number" ? branchOfficeProp : Number(branchOfficeProp);
    }
    const user = getItem("user");
    if (user?.role !== 1) {
      const id = user?.branchOffice?.id;
      return typeof id === "number" ? id : Number(id);
    }
    return undefined;
  }, [branchOfficeProp]);

  useEffect(() => {
    const term = (searchText || "").trim();

    if (term.length < MIN_SEARCH_LEN) {
      // Clear suggestions but keep selected value stable
      setCars([]);
      return;
    }

    const currentSeq = ++requestSeq.current;
    const t = setTimeout(() => {
      // Najprv vlastna pobocka, lebo to je bezny pripad a vysledok je kratky.
      // Ked sa nenajde nic, hlada sa este raz BEZ obmedzenia na pobocku.
      //
      // Preco: v Bratislave bezne obsluhuju auta, ktore su v systeme vedene
      // inde, lebo flotila presuva vozidla medzi krajmi a karta auta sa
      // neprepise. Obsluha potom vidi "No results" pri aute, ktore v systeme
      // je, a nema ako zistit preco. Stalo sa to pri EL959AA (Martin),
      // BL944RE (Kosice) aj AA130XF (Banska Bystrica).
      const hladaj = (ibaVlastnaPobocka) =>
        fetchCars({
          variables: {
            tokenGet,
            search: term,
            offset: 0,
            limit: limit,
            branchOffice: ibaVlastnaPobocka ? branchOfficeForUser : undefined,
            active: true,
          },
        });

      hladaj(true)
        .then((res) => {
          const data = res?.data?.getCars;
          if (Array.isArray(data) && data.length > 0) return data;
          if (!branchOfficeForUser) return data;
          return hladaj(false).then((r2) => r2?.data?.getCars);
        })
        .then((data) => {
          if (currentSeq !== requestSeq.current) return;
          setCars(Array.isArray(data) ? data : []);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(t);
  }, [searchText, fetchCars, tokenGet, branchOfficeForUser, limit]);

  const options = useMemo(() => {
    const map = new Map();
    (cars || []).forEach((c) => {
      if (!c?.id) return;
      map.set(String(c.id), c);
    });
    if (selectedCar?.id) {
      map.set(String(selectedCar.id), selectedCar);
    }
    return Array.from(map.values());
  }, [cars, selectedCar]);

  const notFoundContent = useMemo(() => {
    const term = (searchText || "").trim();
    if (term.length > 0 && term.length < MIN_SEARCH_LEN) {
      return <span>Zadaj aspoň 3 znaky</span>;
    }
    if (loading) {
      return <Spin size="small" />;
    }
    return <span>No results</span>;
  }, [searchText, loading]);

  const renderOptionText = (car) => {
    const lp = car?.licence_plate || "";
    const company = car?.carOwner?.company;
    // Ked je auto vedene na inej pobocke, treba to povedat hned v zozname,
    // inak clovek nechape, odkial sa tam vzalo.
    const pobocka = car?.branchOffice?.company;
    const inaPobocka =
      branchOfficeForUser &&
      car?.branchOffice?.id &&
      Number(car.branchOffice.id) !== Number(branchOfficeForUser)
        ? ` · vedené na: ${pobocka}`
        : "";
    if (showCarOwnerCompany && company) {
      return `${lp} — ${company}${inaPobocka}`;
    }
    return `${lp}${inaPobocka}`;
  };

  return (
    <Select
      {...rest}
      showSearch
      allowClear
      placeholder="Vozidlo: EČV, VIN alebo vodič, aspoň 3 znaky"
      optionFilterProp="children"
      required={required}
      filterOption={false}
      onSearch={(val) => setSearchText(val)}
      notFoundContent={notFoundContent}
      onSelect={(value) => {
        const car = options.find((c) => String(c?.id) === String(value));
        if (car) {
          setSelectedCar(car);
          if (typeof onCarSelected === "function") {
            onCarSelected(car);
          }
        }
      }}
      onClear={() => {
        setSearchText("");
        setCars([]);
      }}
    >
      {options.map((c) => (
        <Option key={c.id} value={c.id} label={c.licence_plate}>
          {renderOptionText(c)}
        </Option>
      ))}
    </Select>
  );
};

export { SelectCarAsync };

